import { RECOMMENDATION_STALENESS_MS } from 'src/constants';
import { UseQueryOptions, UseQueryResult, useQuery } from 'react-query';
import {
    GSLAApiResponse,
    GSLADefinition,
    GSLADefinitionsByMutipleWarehouses,
    GSLADefinitionsByProductionCapability,
    GSLAPublishRequest,
    GSLARecommendationsByMutipleWarehouses,
    GSLARecommendationsDefinitionsByProductionCapability,
    GSLAToPublish,
    GetGSLAForWarehouseResponse,
    GetGSLAResponse,
    GetGSLARecommendationForWarehouseResponse,
    GetGSLARecommendationsResponse,
} from 'src/features/types/gslaApi';
import { GetFCMetadataResponse } from 'src/features/types/fcMetadataTypes';
import { deleteGSLA, getMultipleSitesGSLA, getMultipleSitesGSLARecommendations, getSiteGSLA, getSiteGSLARecommendations, publishGSLAValues, getFCMetadata } from 'src/utils/Queries';

// Adapter functions to convert between new API response format and existing code format
function adaptGSLAForWarehouseResponse( response: GetGSLAForWarehouseResponse | undefined ): GSLADefinitionsByProductionCapability | undefined
{
    if ( !response ) return undefined;

    const result: GSLADefinition[] = [];

    // Extract the GSLA values from the new nested structure
    Object.entries( response.GSLAByHandlingTierByPC ).forEach( ( [ productionCapability, handlingTierMap ] ) =>
    {
        Object.entries( handlingTierMap ).forEach( ( [ handlingTier, gslaValue ] ) =>
        {
            result.push( {
                productionCapability,
                warehouseId: '', // This will be filled by the consuming code
                publishDateEpochMillis: gslaValue.publishDateEpochMillis,
                gslaInSeconds: gslaValue.gslaInSeconds,
                warehouseHandlingTier: handlingTier,
            } );
        } );
    } );

    return {
        publishedGSLAList: result,
    };
}

function adaptMultipleGSLAResponse( response: GetGSLAResponse | undefined ): GSLADefinitionsByMutipleWarehouses | undefined
{
    if ( !response ) return undefined;

    const warehouseToGSLAMap: { [ warehouseId: string ]: GSLADefinition[] } = {};

    // Extract the GSLA values from the new nested structure
    Object.entries( response.warehouseToGSLAMap ).forEach( ( [ warehouseId, pcMap ] ) =>
    {
        const gslaList: GSLADefinition[] = [];

        Object.entries( pcMap ).forEach( ( [ productionCapability, handlingTierMap ] ) =>
        {
            Object.entries( handlingTierMap ).forEach( ( [ handlingTier, gslaValue ] ) =>
            {
                gslaList.push( {
                    productionCapability,
                    warehouseId,
                    publishDateEpochMillis: gslaValue.publishDateEpochMillis,
                    gslaInSeconds: gslaValue.gslaInSeconds,
                    warehouseHandlingTier: handlingTier,
                } );
            } );
        } );

        warehouseToGSLAMap[ warehouseId ] = gslaList;
    } );

    return {
        warehouseToGSLAMap,
    };
}

function adaptGSLARecommendationForWarehouseResponse( response: GetGSLARecommendationForWarehouseResponse | undefined ): GSLARecommendationsDefinitionsByProductionCapability | undefined
{
    if ( !response ) return undefined;

    const gslaList = response.pcToGslaList.map( item => (
        {
            productionCapability: item.productionCapability,
            warehouseId: response.warehouseId,
            publishDateEpochMillis: item.publishDateEpochMillis,
            gslaInSeconds: item.gslaInSeconds,
            // No handling tier for recommendations as they are at the PC level
        }
    ) );

    return {
        pcToGslaList: gslaList,
    };
}

/** Returns true if a recommendation is fresh (within the staleness window) */
export function isRecommendationFresh(publishDateEpochMillis: number): boolean {
    return publishDateEpochMillis > Date.now() - RECOMMENDATION_STALENESS_MS;
}

function adaptGSLARecommendationsResponse( response: GetGSLARecommendationsResponse | undefined ): GSLARecommendationsByMutipleWarehouses | undefined
{
    if ( !response ) return undefined;

    const warehouseToGSLARecommendationMap: { [ warehouseId: string ]: GSLADefinition[] } = {};

    Object.entries( response.warehouseToGSLARecommendationMap ).forEach( ( [ warehouseId, recommendations ] ) =>
    {
        // Filter out stale recommendations and transform to GSLADefinition objects
        const gslaList: GSLADefinition[] = recommendations
            .filter(item => isRecommendationFresh(item.publishDateEpochMillis))
            .map( item => (
                {
                    productionCapability: item.productionCapability,
                    warehouseId,
                    publishDateEpochMillis: item.publishDateEpochMillis,
                    gslaInSeconds: item.gslaInSeconds,
                    // No handling tier for recommendations as they are at the PC level
                }
            ) );

        warehouseToGSLARecommendationMap[ warehouseId ] = gslaList;
    } );

    return {
        warehouseToGSLARecommendationMap,
    };
}

export function getGSLADataForWarehouse(
    siteId: string,
    preserveDefaultTypes?: boolean,
    queryOptions?: Omit<UseQueryOptions<GSLADefinitionsByProductionCapability | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GSLADefinitionsByProductionCapability | undefined>
{
    const cacheKeys = ['gsla-single-warehouse', siteId, preserveDefaultTypes?.toString() || 'undefined'];
    return useQuery<GSLADefinitionsByProductionCapability | undefined>(
        cacheKeys,
        async () =>
        {
            const response = await getSiteGSLA(siteId, preserveDefaultTypes);
            return adaptGSLAForWarehouseResponse( response );
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}

/**
 * Calls the single-warehouse API but returns data in the multi-warehouse format expected by GSLAHierarchicalTableAdapter.
 * This allows the GSLA page to show all CDS PCs with WHTs propagated from defaults.
 */
export function getGSLADataForWarehouseAsMulti(
    siteId: string,
    preserveDefaultTypes?: boolean,
    queryOptions?: Omit<UseQueryOptions<GSLADefinitionsByMutipleWarehouses | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GSLADefinitionsByMutipleWarehouses | undefined> {
    const cacheKeys = ['gsla-single-as-multi', siteId, preserveDefaultTypes?.toString() || 'undefined'];
    return useQuery<GSLADefinitionsByMutipleWarehouses | undefined>(
        cacheKeys,
        async () => {
            if (!siteId) return undefined;

            // Fetch both the site-specific and DEFAULT warehouse data in parallel
            const [siteResponse, defaultResponse] = await Promise.all([
                getSiteGSLA(siteId, preserveDefaultTypes),
                getSiteGSLA('DEFAULT', preserveDefaultTypes),
            ]);

            const siteAdapted = adaptGSLAForWarehouseResponse(siteResponse);
            const defaultAdapted = adaptGSLAForWarehouseResponse(defaultResponse);

            const warehouseToGSLAMap: { [warehouseId: string]: GSLADefinition[] } = {};

            if (siteAdapted) {
                warehouseToGSLAMap[siteId] = siteAdapted.publishedGSLAList.map(def => ({ ...def, warehouseId: siteId }));
            }
            if (defaultAdapted) {
                warehouseToGSLAMap['DEFAULT'] = defaultAdapted.publishedGSLAList.map(def => ({ ...def, warehouseId: 'DEFAULT' }));
            }

            if (Object.keys(warehouseToGSLAMap).length === 0) return undefined;

            return { warehouseToGSLAMap };
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}

export function getGSLADataForMultipleWarehouses(
    listOfSites: string[],
    preserveDefaultTypes?: boolean,
    queryOptions?: Omit<UseQueryOptions<GSLADefinitionsByMutipleWarehouses | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GSLADefinitionsByMutipleWarehouses | undefined>
{
    const cacheKeys = [ 'gsla-multiple-warehouses', ...listOfSites, preserveDefaultTypes?.toString() || 'undefined' ];
    return useQuery<GSLADefinitionsByMutipleWarehouses | undefined>(
        cacheKeys,
        async () =>
        {
            const response = await getMultipleSitesGSLA( listOfSites, preserveDefaultTypes );
            return adaptMultipleGSLAResponse( response );
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}

export function getGSLARecommendationsForWarehouse(
    siteId: string,
    queryOptions?: Omit<UseQueryOptions<GSLARecommendationsDefinitionsByProductionCapability | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GSLARecommendationsDefinitionsByProductionCapability | undefined>
{
    const cacheKeys = [ 'gsla-recommendations-single', siteId ];
    return useQuery<GSLARecommendationsDefinitionsByProductionCapability | undefined>(
        cacheKeys,
        async () =>
        {
            const response = await getSiteGSLARecommendations( siteId );
            return adaptGSLARecommendationForWarehouseResponse( response );
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}

export function getGSLARecommendationsForMultipleWarehouses(
    listOfSites: string[],
    queryOptions?: Omit<UseQueryOptions<GSLARecommendationsByMutipleWarehouses | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GSLARecommendationsByMutipleWarehouses | undefined>
{
    const cacheKeys = [ 'gsla-recommendations-multiple', ...listOfSites ];
    return useQuery<GSLARecommendationsByMutipleWarehouses | undefined>(
        cacheKeys,
        async () =>
        {
            const response = await getMultipleSitesGSLARecommendations( listOfSites );
            return adaptGSLARecommendationsResponse( response );
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}

/**
 * Deletes a GSLA entry for a warehouse, production capability, and optionally a specific handling tier
 * If warehouseHandlingTier is not specified, all handling tiers for the given production capability will be deleted
 * 
 * @param warehouseId The warehouse ID
 * @param productionCapability The production capability
 * @param userAlias User initiating the deletion
 * @param actionSource Source of the delete action
 * @param reason Reason for deletion
 * @param warehouseHandlingTier Optional handling tier (if not specified, deletes entire PC)
 * @returns Promise that resolves when deletion is complete
 */
export async function deleteGSLAEntry(
    warehouseId: string,
    productionCapability: string,
    userAlias: string,
    actionSource: string,
    reason: string,
    warehouseHandlingTier?: string
): Promise<void> {
    await deleteGSLA({
        warehouseId,
        productionCapability,
        warehouseHandlingTier,
        userAlias,
        actionSource,
        reason
    });
}

export function useFCMetadata(
    warehouseId: string,
    queryOptions?: Omit<UseQueryOptions<GetFCMetadataResponse | undefined>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetFCMetadataResponse | undefined>
{
    const cacheKeys = [ 'fc-metadata', warehouseId ];
    return useQuery<GetFCMetadataResponse | undefined>(
        cacheKeys,
        async () =>
        {
            if ( !warehouseId ) return undefined;
            return await getFCMetadata( warehouseId );
        },
        {
            staleTime: Infinity,
            ...queryOptions,
        },
    );
}
